/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MapPin, ArrowUpRight } from "lucide-react";
import { PURE_SOULS_URL } from "@/lib/site";

const links = [
  { to: "/what-is-water-cremation", label: "What Is It" },
  { to: "/benefits", label: "Benefits" },
  { to: "/the-process", label: "The Process" },
  { to: "/providers", label: "Find A Provider" },
  { to: "/articles", label: "Learning Centre" },
  { to: "/faq", label: "FAQ" },
];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close on navigation
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock scroll while the mobile menu is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className="sticky top-0 z-50 bg-[#F9F9F6]/80 backdrop-blur-xl border-b border-sand-300/50 shadow-soft"
        data-testid="site-navigation"
      >
        <div className="container-editorial flex items-center justify-between py-5">
          <Link to="/" className="group flex items-center gap-3" data-testid="nav-logo-link">
            <span className="inline-block h-2 w-2 rounded-full bg-clay-500 group-hover:bg-clay-600 transition-colors" />
            <span className="font-serif text-xl sm:text-2xl tracking-tight text-forest-900">
              Pet Water Cremation
              <span className="text-sage-600"> Australia</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace(/\//g, "")}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-clay-500 after:transition-all after:duration-300 ${
                    isActive
                      ? "text-forest-900 after:w-full"
                      : "text-forest-700 hover:text-forest-900 after:w-0 hover:after:w-full"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden text-forest-900 h-11 w-11 -mr-2 flex items-center justify-center rounded-full active:bg-sand-200 transition-colors"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile menu rendered OUTSIDE the blurred header so its fixed
          positioning is relative to the viewport (a backdrop-filter ancestor
          would otherwise become its containing block and collapse it). */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden fixed inset-x-0 top-[73px] bottom-0 z-40 bg-[#F9F9F6] overflow-y-auto"
            data-testid="nav-mobile-menu"
          >
            <div className="container-editorial py-8 flex flex-col min-h-full">
              <nav className="flex flex-col">
                {links.map((l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 + i * 0.05, duration: 0.35 }}
                  >
                    <NavLink
                      to={l.to}
                      data-testid={`nav-mobile-link-${l.to.replace(/\//g, "")}`}
                      className={({ isActive }) =>
                        `flex items-center justify-between border-b border-sand-300/70 py-5 font-serif text-3xl tracking-tight ${
                          isActive ? "text-clay-600" : "text-forest-900"
                        }`
                      }
                    >
                      {l.label}
                      <ArrowUpRight size={22} className="text-forest-300" />
                    </NavLink>
                  </motion.div>
                ))}
              </nav>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.4 }}
                className="mt-auto pt-10 flex flex-col gap-3"
              >
                <Link to="/providers" className="btn-primary text-base py-4" data-testid="nav-mobile-cta-find">
                  <MapPin size={18} /> Find A Provider
                </Link>
                <a
                  href={PURE_SOULS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary text-base py-4"
                  data-testid="nav-mobile-cta-puresouls"
                >
                  Visit Pure Souls <ArrowUpRight size={17} />
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
